import React, { useState } from "react";
import PageShell from "@/components/PageShell";
import { Plus, Minus } from "lucide-react";

const FAQS = [
    {
        cat: "Orders",
        items: [
            {
                q: "When will my order ship?",
                a: "Orders are packed and dispatched within 2 business days from our East District studio. You'll receive a tracking link by email the moment it leaves us.",
            },
            {
                q: "Can I edit or cancel my order?",
                a: "We move fast — once an order is paid, we have a ~30 minute window before it's in the queue. Email hello@uknitwear.com immediately and we'll do what we can.",
            },
            {
                q: "Do you restock sold-out pieces?",
                a: "No. Every drop is a single run of ~1,000 units. When it's gone, it stays gone. We do release new colourways across seasons.",
            },
        ],
    },
    {
        cat: "Shipping",
        items: [
            {
                q: "Do you ship worldwide?",
                a: "Yes — to 47 countries. Express tracked is the default. Duties and taxes are calculated at checkout (DDP) for the EU, UK, US, Canada, Japan, and Australia.",
            },
            {
                q: "Is shipping free?",
                a: "Free standard shipping on orders over $200. Express upgrades available at checkout.",
            },
            {
                q: "How long does delivery take?",
                a: "UK: 1–2 days. EU: 2–4 days. US/Canada: 3–6 days. Rest of world: 5–10 days.",
            },
        ],
    },
    {
        cat: "Returns",
        items: [
            {
                q: "What's your return policy?",
                a: "30 days from delivery for unworn, unwashed pieces with all tags attached. Sale items and final-sale pieces are non-returnable.",
            },
            {
                q: "Who pays for return shipping?",
                a: "We cover return shipping for UK and EU customers. For other regions, return shipping is paid by the customer unless the item is faulty.",
            },
        ],
    },
    {
        cat: "Product",
        items: [
            {
                q: "How does your sizing run?",
                a: "Our knitwear is cut boxy and intentionally oversized. If you're between sizes, size down. See the full size guide for measurements.",
            },
            {
                q: "Where is UKnitwear made?",
                a: "Knitwear and tops are constructed in Portugal. Denim and selvedge bottoms are made in Okayama, Japan. Hardware is sourced from Italy.",
            },
            {
                q: "Are materials sustainable?",
                a: "85% of our fabric is GOTS-certified organic cotton or recycled wool. We're transparent about the other 15% — see each product page for the spec sheet.",
            },
        ],
    },
];

const Item = ({ q, a, id }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b-2 border-dotted border-ink/60" data-testid={`faq-item-${id}`}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-4 py-5 text-left hover:text-hazard"
                aria-expanded={open}
                data-testid={`faq-toggle-${id}`}
            >
                <span className="font-heading text-2xl md:text-3xl uppercase leading-none">
                    {q}
                </span>
                {open ? <Minus className="w-5 h-5 flex-shrink-0" /> : <Plus className="w-5 h-5 flex-shrink-0" />}
            </button>
            {open && (
                <p className="pb-6 max-w-3xl text-sm md:text-base leading-relaxed text-foreground/80">
                    {a}
                </p>
            )}
        </div>
    );
};

const FAQ = () => {
    return (
        <PageShell
            kicker="[Common Questions]"
            title="Frequently"
            accent="Asked."
            intro="Everything we get asked, answered in one place. Still stuck? Hit us up via the contact page."
            testid="faq-page"
        >
            {FAQS.map((group, gi) => (
                <section key={group.cat} className="px-4 md:px-8 py-10 border-b-4 border-ink">
                    <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                        // 0{gi + 1}
                    </span>
                    <h2 className="font-heading text-4xl md:text-5xl uppercase leading-none mt-1 mb-4">
                        {group.cat}
                    </h2>
                    <div>
                        {group.items.map((it, i) => (
                            <Item key={i} q={it.q} a={it.a} id={`${gi}-${i}`} />
                        ))}
                    </div>
                </section>
            ))}
        </PageShell>
    );
};

export default FAQ;
